/**
 * Interface primitives.
 *
 * Bright, high-contrast operational primitives. White surfaces, strong type and
 * deliberate lime/violet/cyan accents keep dense workspaces readable without a
 * dark shell. Every interactive element remains keyboard reachable.
 */

import { clsx } from 'clsx';
import type { ButtonHTMLAttributes, HTMLAttributes, ReactNode } from 'react';

type Tone = 'neutral' | 'brand' | 'ok' | 'warn' | 'danger' | 'info';

const TONE_SOLID: Record<Tone, string> = {
  neutral: 'bg-surface-3 text-fg hover:bg-line-strong/35',
  brand: 'bg-brand text-brand-fg shadow-sm hover:brightness-95',
  ok: 'bg-ok text-white shadow-sm hover:brightness-95',
  warn: 'bg-warn text-black hover:opacity-90',
  danger: 'bg-danger text-white hover:opacity-90',
  info: 'bg-info text-white hover:opacity-90',
};

const TONE_SOFT: Record<Tone, string> = {
  neutral: 'bg-surface-2 text-muted border-line',
  brand: 'bg-brand-soft text-brand border-brand/30',
  ok: 'bg-ok-soft text-ok border-ok/30',
  warn: 'bg-warn-soft text-warn border-warn/30',
  danger: 'bg-danger-soft text-danger border-danger/30',
  info: 'bg-info-soft text-info border-info/30',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  tone?: Tone;
  variant?: 'solid' | 'outline' | 'ghost';
  size?: 'sm' | 'md';
  shortcut?: string;
}

export function Button({
  tone = 'neutral',
  variant = 'outline',
  size = 'md',
  shortcut,
  className,
  children,
  ...props
}: ButtonProps) {
  return (
    <button
      type="button"
      className={clsx(
        'inline-flex items-center justify-center gap-1.5 rounded-md border font-medium',
        'transition-[transform,background-color,border-color,color,filter] duration-base active:scale-[0.97]',
        'disabled:cursor-not-allowed disabled:opacity-45 disabled:active:scale-100',
        size === 'sm' ? 'h-8 px-2.5 text-sm' : 'h-9 px-3.5 text-base',
        variant === 'solid' && clsx('border-transparent', TONE_SOLID[tone]),
        variant === 'outline' && clsx('border-line-strong/60 bg-surface text-fg shadow-sm hover:border-focus/50 hover:bg-surface-2'),
        variant === 'ghost' && 'border-transparent bg-transparent text-muted hover:bg-surface-2 hover:text-fg',
        className,
      )}
      {...props}
    >
      {children}
      {shortcut ? (
        <kbd className="knx-num ml-1 rounded-sm border border-line px-1 text-2xs text-faint">
          {shortcut}
        </kbd>
      ) : null}
    </button>
  );
}

export function Badge({
  tone = 'neutral',
  children,
  className,
}: {
  tone?: Tone;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-semibold tracking-wide',
        TONE_SOFT[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

export function Panel({
  title,
  actions,
  children,
  className,
  bodyClassName,
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
}) {
  return (
    <section
      className={clsx('flex min-h-0 flex-col overflow-hidden rounded-lg border border-line bg-surface shadow-sm', className)}
    >
      {title ? (
        <header className="flex h-10 shrink-0 items-center justify-between border-b border-line bg-surface-2/70 px-3">
          <h2 className="text-sm font-bold tracking-wide text-fg">{title}</h2>
          {actions ? <div className="flex items-center gap-1">{actions}</div> : null}
        </header>
      ) : null}
      <div className={clsx('min-h-0 flex-1 overflow-auto', bodyClassName)}>{children}</div>
    </section>
  );
}

export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-line/60 px-2 py-1.5 last:border-b-0">
      <span className="shrink-0 text-sm font-medium text-muted">{label}</span>
      <span className="truncate text-right text-base text-fg">{children}</span>
    </div>
  );
}

export function Metric({
  label,
  value,
  tone = 'neutral',
}: {
  label: string;
  value: ReactNode;
  tone?: Tone;
}) {
  return (
    <div className="flex flex-col gap-0.5 border-r border-line px-3 py-1 last:border-r-0">
      <span className="text-xs font-semibold uppercase tracking-wide text-muted">{label}</span>
      <span
        className={clsx(
          'knx-num text-md font-medium',
          tone === 'danger' && 'text-danger',
          tone === 'warn' && 'text-warn',
          tone === 'ok' && 'text-ok',
        )}
      >
        {value}
      </span>
    </div>
  );
}

export function EmptyState({ message, hint }: { message: string; hint?: string }) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-1.5 p-6 text-center">
      <span className="mb-1 h-1 w-10 rounded-full bg-brand" aria-hidden="true" />
      <p className="text-md font-semibold text-fg">{message}</p>
      {hint ? <p className="max-w-sm text-sm text-muted">{hint}</p> : null}
    </div>
  );
}

export function LoadingState({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="flex h-full items-center justify-center p-6" role="status" aria-live="polite">
      <div className="w-40 overflow-hidden rounded-sm bg-surface-2">
        <div className="h-0.5 w-1/3 animate-knx-indeterminate bg-brand" />
      </div>
      <span className="sr-only">{label}</span>
    </div>
  );
}

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-2 p-6 text-center">
      <p className="text-base text-danger">{message}</p>
      {onRetry ? (
        <Button size="sm" onClick={onRetry}>
          Retry
        </Button>
      ) : null}
    </div>
  );
}

export function Row({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={clsx(
        'flex h-row items-center gap-2 border-b border-line/60 px-2 text-base',
        className,
      )}
      {...props}
    />
  );
}

export const STATE_TONE: Record<string, Tone> = {
  offline: 'neutral',
  available: 'ok',
  reserved: 'info',
  ringing: 'info',
  on_call: 'brand',
  hold: 'warn',
  acw: 'warn',
  break: 'neutral',
  lunch: 'neutral',
  meeting: 'neutral',
  training: 'neutral',
  coaching: 'neutral',
  technical_issue: 'danger',
  custom: 'neutral',
};
